/**
 * Workspaces: search, open, set. The list persists only workspaces that
 * differ from the defaults (US only or pinned); Open drops any other
 * workspace in, expanded at its live state. One item is open at a time.
 */

import { useMemo, useState } from "react";
import { Button } from "../../../ui/button.js";
import { Input } from "../../../ui/input.js";
import type {
  CatalogModel,
  Lane,
  LaneState,
  RoutedWorkspace,
  RoutingPolicy,
  Tier,
  WorkspaceLite,
} from "./types.js";
import { workspaceLabel } from "./format.js";
import { PRINT_CARD_CLASS, PRINT_CARD_STYLE } from "./styles.js";
import { WorkspaceItem } from "./WorkspaceItem.js";

interface WorkspacesCardProps {
  workspaces: RoutedWorkspace[];
  allWorkspaces: WorkspaceLite[];
  catalog: Record<Lane, CatalogModel[]>;
  lanes: LaneState[];
  policy: RoutingPolicy;
  tiers: Tier[];
  onChanged: () => Promise<void>;
  onError: (message: string | null) => void;
}

const DATALIST_ID = "model-routing-workspaces";

export function WorkspacesCard({
  workspaces,
  allWorkspaces,
  catalog,
  lanes,
  policy,
  tiers,
  onChanged,
  onError,
}: WorkspacesCardProps) {
  const [query, setQuery] = useState("");
  const [openedIds, setOpenedIds] = useState<string[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const routedIds = useMemo(() => new Set(workspaces.map((w) => w.workspaceId)), [workspaces]);

  // Opened-from-search workspaces that follow the defaults lead the list,
  // then every workspace that differs from them.
  const items = useMemo(() => {
    const opened: RoutedWorkspace[] = [];
    for (const id of openedIds) {
      if (routedIds.has(id)) continue;
      const ws = allWorkspaces.find((w) => w.id === id);
      if (ws) {
        opened.push({ workspaceId: ws.id, name: ws.name, slug: ws.slug, dataResidency: null, pins: [] });
      }
    }
    return [...opened, ...workspaces];
  }, [openedIds, routedIds, allWorkspaces, workspaces]);

  const usOnly = workspaces.filter((w) => w.dataResidency === "us").length;
  const pinned = workspaces.filter((w) => w.pins.length > 0).length;

  function keep(id: string) {
    setOpenedIds((ids) => (ids.includes(id) ? ids : [id, ...ids]));
  }

  function openFromSearch() {
    const q = query.trim().toLowerCase();
    if (!q) return;
    const match =
      allWorkspaces.find((w) => workspaceLabel(w).toLowerCase() === q) ??
      allWorkspaces.find((w) => w.slug.toLowerCase() === q) ??
      allWorkspaces.find((w) => w.name.toLowerCase() === q);
    if (!match) {
      setNotFound(true);
      return;
    }
    setNotFound(false);
    keep(match.id);
    setOpenId(match.id);
    setQuery("");
  }

  return (
    <section aria-label="Workspaces" className={PRINT_CARD_CLASS} style={PRINT_CARD_STYLE}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
          Workspaces
        </h3>
        <span
          className="rounded-[var(--radius-pill)] border px-2.5 py-0.5 text-[11px]"
          style={{
            borderColor: "var(--border-subtle)",
            color: "var(--text-secondary)",
            fontFamily: "var(--font-mono)",
          }}
        >
          {`${allWorkspaces.length} workspaces · ${usOnly} US only · ${pinned} pinned`}
        </span>
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>
        Every workspace starts unrestricted on the lane order above. Open one to set its policy
        or pin a tier. Listed here: workspaces that are US only or hold a pin.
      </p>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          aria-label="Search workspaces"
          inputSize="sm"
          list={DATALIST_ID}
          value={query}
          state={notFound ? "error" : "default"}
          onChange={(e) => {
            setQuery(e.target.value);
            setNotFound(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              openFromSearch();
            }
          }}
          placeholder="Search workspaces by name or slug"
          className="min-w-[260px] max-w-[420px] flex-1"
        />
        <datalist id={DATALIST_ID}>
          {allWorkspaces.map((ws) => (
            <option key={ws.id} value={workspaceLabel(ws)} />
          ))}
        </datalist>
        <Button type="button" size="sm" variant="secondary" onClick={openFromSearch} disabled={!query.trim()}>
          Open
        </Button>
        {notFound && (
          <span className="text-xs" role="status" style={{ color: "var(--error)" }}>
            No workspace matches. Pick one from the list.
          </span>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-xs" style={{ color: "var(--text-tertiary)" }}>
          Every workspace follows the defaults. Search above to open one.
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {items.map((ws) => {
            const defaultOnly = !routedIds.has(ws.workspaceId);
            const opened = openedIds.includes(ws.workspaceId);
            return (
              <WorkspaceItem
                key={ws.workspaceId}
                workspace={ws}
                catalog={catalog}
                lanes={lanes}
                policy={policy}
                tiers={tiers}
                open={openId === ws.workspaceId}
                onToggle={() => {
                  if (openId === ws.workspaceId) {
                    setOpenId(null);
                  } else {
                    // Keep it listed while open, so an Undo back to the
                    // defaults does not drop it from under the operator.
                    keep(ws.workspaceId);
                    setOpenId(ws.workspaceId);
                  }
                }}
                onClose={
                  opened && defaultOnly
                    ? () => {
                        setOpenedIds((ids) => ids.filter((id) => id !== ws.workspaceId));
                        if (openId === ws.workspaceId) setOpenId(null);
                      }
                    : undefined
                }
                onChanged={onChanged}
                onError={onError}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
